import React from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

const STATE_STYLES = {
  pending: { label: 'Waiting', color: '#8A94A6', background: '#F1F3F6' },
  running: { label: 'In progress', color: '#4698D3', background: '#EBF5FB' },
  completed: { label: 'Done', color: '#2E7D4F', background: '#E8F5EC' },
  failed: { label: 'Failed', color: '#C0392B', background: '#FDECEA' },
  // Deliberately not run (e.g. the policy sweep on a dry run). Without its own
  // style it would fall through to "Waiting" and look like a step that never
  // got there.
  skipped: { label: 'Skipped', color: '#8A94A6', background: '#F1F3F6' },
};

const formatCount = (n) => (n ?? 0).toLocaleString();

const formatElapsed = (seconds) => {
  if (!seconds || seconds < 60) return `${seconds || 0}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
};

/**
 * Live per-resource counters for a long-running fetch or replication job.
 *
 * A resource whose total is still 0 while it runs has a genuinely unknown size —
 * AWS paginators do not report a count up front — so it gets an indeterminate
 * bar and a bare running count rather than a misleading "n / 0".
 */
const JobProgressPanel = ({ title, subtitle, progress }) => {
  if (!progress || !progress.resources?.length) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E5E5',
        borderRadius: '12px',
        p: 3,
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#253E66',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.8rem',
                color: '#666666',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Chip
          label={`Elapsed ${formatElapsed(progress.elapsedSeconds)}`}
          size="small"
          sx={{
            backgroundColor: '#F1F3F6',
            color: '#253E66',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 500,
            fontSize: '0.7rem',
          }}
        />
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Resource', 'Progress', 'Count', 'Status'].map((heading, index) => (
                <TableCell
                  key={heading}
                  align={index >= 2 ? 'right' : 'left'}
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: '#666666',
                    borderBottom: '1px solid #E5E5E5',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {progress.resources.map((resource) => {
              const baseState = STATE_STYLES[resource.state] || STATE_STYLES.pending;
              // A finished row that had failures is not a clean "Done". Counts
              // include failures so the bar reaches 100% rather than looking
              // stalled, which makes a green "Done" directly above "3 failed"
              // read as a contradiction — say "completed with errors" instead.
              const state =
                resource.state === 'completed' && resource.failed > 0
                  ? {
                      label: `Done, ${formatCount(resource.failed)} failed`,
                      color: '#B7791F',
                      background: '#FEF5E7',
                    }
                  : baseState;
              const sizeKnown = resource.total > 0;
              const percent = sizeKnown
                ? Math.min(100, Math.round((resource.done / resource.total) * 100))
                : 0;
              // Only an actively running row of unknown size should animate;
              // a pending row with no total yet is simply not started.
              const indeterminate = !sizeKnown && resource.state === 'running';

              return (
                <TableRow key={resource.key}>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.85rem',
                      color: '#253E66',
                      fontWeight: 500,
                      borderBottom: '1px solid #F1F3F6',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {resource.label || resource.key}
                    {(resource.failed > 0 || resource.skipped > 0) && (
                      <Typography
                        component="span"
                        sx={{
                          display: 'block',
                          fontFamily: 'Metropolis, sans-serif',
                          fontSize: '0.7rem',
                          color: '#8A94A6',
                        }}
                      >
                        {/* The count column shows work *finished*, which includes
                            failures — so spell out the successes too rather than
                            leaving "7 / 7" next to "3 failed" to be reconciled. */}
                        {[
                          `${formatCount(resource.done - resource.failed - (resource.skipped || 0))} succeeded`,
                          resource.failed > 0 ? `${formatCount(resource.failed)} failed` : null,
                          resource.skipped > 0 ? `${formatCount(resource.skipped)} skipped` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #F1F3F6', minWidth: 140 }}>
                    <LinearProgress
                      variant={indeterminate ? 'indeterminate' : 'determinate'}
                      value={percent}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#F1F3F6',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          backgroundColor: state.color,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.8rem',
                      color: '#253E66',
                      fontVariantNumeric: 'tabular-nums',
                      borderBottom: '1px solid #F1F3F6',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sizeKnown
                      ? `${formatCount(resource.done)} / ${formatCount(resource.total)}`
                      : formatCount(resource.done)}
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: '1px solid #F1F3F6' }}>
                    <Chip
                      label={state.label}
                      size="small"
                      sx={{
                        backgroundColor: state.background,
                        color: state.color,
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default JobProgressPanel;
